<script setup lang="ts">
import UserAvatarComponent from '@toolify/client/src/modules/entities/user/UserAvatarComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {useBrowserTabs} from '@toolify/client/src/modules/broadcast/composables/useBrowserTabs'
import {computed, onBeforeUnmount, onMounted, ref} from 'vue'
import someoneCallingSound from '@toolify/client/src/assets/sounds/someone-calling.wav'
import {
  ICallInviteResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ICallInviteResponseInstance'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {IConversationTypeInterfacesMap} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {useCallItemStore} from '@toolify/client/src/stores/CallItemStore/useCallItemStore'
import {useCallInviteApiStore} from '@toolify/client/src/stores/api/CallInviteApiStore/useCallInviteApiStore'
import {useRouter} from 'vue-router'
import {
  getActiveCallRouteLocation,
} from '@toolify/client/src/modules/call/functions/getActiveCallRouteLocation'

interface Props {
  callInvite: ICallInviteResponseInstance
}
const props = defineProps<Props>()

const callItemStore = useCallItemStore()
const callInviteApiStore = useCallInviteApiStore()
const router = useRouter()
const {isPrimaryTab} = useBrowserTabs()


const audio = ref(null)
const shouldPlaySound = ref(true)
const isJoining = ref(false)
const isRejecting = ref(false)

onMounted(() => {
  if(!isPrimaryTab.value) {
    return
  }
  audio.value = new Audio(someoneCallingSound)
  playAudioInLoop()
})

onBeforeUnmount(() => {
  shouldPlaySound.value = false
  if(audio.value) {
    audio.value.pause()
  }
})

async function playAudioInLoop() {
  if(!shouldPlaySound.value) {
    return
  }
  audio.value.currentTime = 0
  try {
    await audio.value.play()
  } catch(error) {
    // Do nothing
  }

  setTimeout(() => {
    playAudioInLoop()
  }, 2500)
}

const CallDetailsByConversationType = {
  [ConversationType.Workspace](payload: IConversationTypeInterfacesMap[ConversationType.Workspace]['notificationPayload']) {
    return {
      contextPath: [payload.workspaceConversationName],
      contextName: payload.workspaceName,
      contextIconName: IconNameType.Message,
    }
  },
  [ConversationType.Direct](payload: IConversationTypeInterfacesMap[ConversationType.Direct]['notificationPayload']) {
    return {
      contextPath: null,
      contextName: null,
      contextIconName: null,
    }
  },
  [ConversationType.CallRoom](payload: IConversationTypeInterfacesMap[ConversationType.CallRoom]['notificationPayload']) {
    return {
      contextPath: [payload.callRoomName],
      contextName: null,
      contextIconName: IconNameType.Phone,
    }
  },
}

const callDetails = computed(() => {
  if(props.callInvite.call.conversationType === ConversationType.Workspace) {
    return CallDetailsByConversationType[ConversationType.Workspace](
      props.callInvite.conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.Workspace]['notificationPayload'],
    )
  }
  if(props.callInvite.call.conversationType === ConversationType.Direct) {
    return CallDetailsByConversationType[ConversationType.Direct](
      props.callInvite.conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.Direct]['notificationPayload'],
    )
  }
  return CallDetailsByConversationType[ConversationType.CallRoom](
    props.callInvite.conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.CallRoom]['notificationPayload'],
  )
})

const contextPathText = computed(() => {
  return (callDetails.value.contextPath || []).join(' / ')
})


async function onJoin() {
  stopPlayingAudio()
  isJoining.value = true
  const conversationPayload = props.callInvite.call.conversationPayload as IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload']
  await callInviteApiStore.acceptCallInvite(props.callInvite.id)
  const callRouteLocation = getActiveCallRouteLocation(
    props.callInvite.call.conversationType,
    props.callInvite.conversationNotificationPayload,
  )
  if(callRouteLocation) {
    await router.push(callRouteLocation).catch(() => undefined)
  }
  callItemStore.ensureCallItem(props.callInvite.call.conversationType, conversationPayload)
  await callItemStore.join(props.callInvite.call.conversationType, conversationPayload)
}

async function onReject() {
  stopPlayingAudio()
  isRejecting.value = true
  await callInviteApiStore.declineCallInvite(props.callInvite.id)
}

function stopPlayingAudio() {
  shouldPlaySound.value = false
  if(audio.value) {
    audio.value.pause()
  }
}
</script>

<template>
  <div class="incoming-call-overlay">
    <Transition name="container-transition" appear>
      <div class="container">
        <div class="avatar-wrapper">
          <div class="avatars">
            <UserAvatarComponent :entity="props.callInvite.call.authorEntity"/>
          <!--          <UserAvatarComponent :entity="null"/>-->
          <!--          <UserAvatarComponent :entity="null"/>-->
          <!--          <div class="more-entities-indicator">-->
          <!--            +3-->
          <!--          </div>-->
          </div>
        </div>
        <div class="details">
          <div class="title">{{getEntityNameByEntity(props.callInvite.call.authorEntity)}}</div>
          <div class="description">is calling you</div>
        </div>
        <div class="context" v-if="callDetails.contextName">
          <IconComponent :name="callDetails.contextIconName" :weightType="IconWeightType.Solid"/>
          <div class="context-details">
            <div class="context-title">{{callDetails.contextName}}</div>
            <div class="context-description">{{contextPathText}}</div>
          </div>
        </div>
        <div class="actions">
          <ButtonComponent
            :iconNameType="IconNameType.Phone"
            :iconWeightType="IconWeightType.Solid"
            :color="ColorVariantType.Primary"
            @click="onJoin"
            :isLoading="isJoining"
            :isDisabled="isRejecting"
          >
            Join
          </ButtonComponent>
          <ButtonComponent
            :iconNameType="IconNameType.PhoneHangup"
            :iconWeightType="IconWeightType.Solid"
            :color="ColorVariantType.Error"
            @click="onReject"
            :isLoading="isRejecting"
            :isDisabled="isJoining"
          >
            Reject
          </ButtonComponent>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables.scss' as variables;

  .incoming-call-overlay {
    width: 100%;
    height: 100%;
    position: fixed;
    left: 0;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999999999;
    pointer-events: none;
  }

  .avatars {
    display: flex;
  }

  .more-entities-indicator {
    height: 64px;
    aspect-ratio: 1/1;
    background: map-get(variables.$colors, container-highest);
    border-radius: 50%;
    margin-left: -10px;
    border: 4px solid map-get(variables.$colors, container);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: map-get(variables.$textSizes, text-2xl);
    font-weight: 600;
  }

  .details {
    display: grid;
    gap: map-get(variables.$spacing, space-0-5);
  }

  .title {
    font-size: map-get(variables.$textSizes, text-lg);
    font-weight: 600;
    text-align: center;
  }

  .description {
    color: map-get(variables.$colors, content-dim);
    text-align: center;
  }

  .context {
    background: map-get(variables.$colors, container-high);
    border-radius: variables.$borderRadius;
    padding: map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-3) map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-4);
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: map-get(variables.$spacing, space-4);
    margin-top: 16px;

    :deep(.icon-content) {
      font-size: map-get(variables.$textSizes, text-lg) !important;
    }
  }

  .context-title {
    color: map-get(variables.$colors, content-dim);
    font-size: map-get(variables.$textSizes, text-base);
  }

  .context-description {
    font-weight: 600;
  }

  .avatar-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.user-avatar) {
      height: 64px;
      
      margin-left: -10px;
      border: 4px solid map-get(variables.$colors, container);

      &:first-child {
        margin-left: 0;
      }
    }
  }

  .container {
    border-radius: variables.$borderRadius;
    width: 320px;
    max-width: calc(100vw - 2rem);
    background: map-get(variables.$colors, container);
    padding: map-get(variables.$spacing, space-4);
    display: grid;
    gap: map-get(variables.$spacing, space-2);
    box-sizing: border-box;
    pointer-events: all;
    box-shadow: variables.$containerShadow;
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: map-get(variables.$spacing, space-2);
    margin-top: 16px;

    &:deep(.button.is-disabled) {
      background: map-get(variables.$colors, container-high) !important;
    }
  }

  .container-transition {
    &-enter-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-leave-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-enter-active,
    &-leave-active {
      transform: none;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(5%) scale(0.85);
    }
  }
</style>

